import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Clock, Send, LogOut, ShieldCheck } from "lucide-react";
import { LOGO_HORIZONTAL } from "@/lib/brand";

export default function Pending() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login", { replace: true }); return; }
    if (user.status === "active") { navigate("/dashboard", { replace: true }); return; }
    if (user.status === "rejected") return;
    api.get("/access-request/status").then((r) => { if (r.data.request) setSent(true); }).catch(() => {});
  }, [user, loading, navigate]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/access-request", { phone, message });
      setSent(true);
      toast.success("Solicitud enviada. Te contactaremos pronto.");
    } catch (e) { toast.error("No se pudo enviar la solicitud"); }
    finally { setSubmitting(false); }
  };

  const rejected = user?.status === "rejected";

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4">
      <div className="w-full max-w-md bg-white rounded-3xl border border-neutral-200 shadow-[0_20px_60px_rgba(0,0,0,0.08)] p-8 animate-fade-up">
        <img src={LOGO_HORIZONTAL} alt="MCTECH EVENTOS" className="h-11 w-auto mx-auto" />
        <div className="mt-6 flex items-center justify-center">
          <div className={`h-14 w-14 rounded-2xl flex items-center justify-center ${rejected ? "bg-red-50 text-red-500" : "bg-amber-50 text-amber-500"}`}>
            <Clock size={28} />
          </div>
        </div>
        <h1 className="font-display font-extrabold text-xl text-center mt-4">
          {rejected ? "Solicitud no aprobada" : "Cuenta pendiente de aprobación"}
        </h1>
        <p className="text-neutral-500 text-center mt-2 text-sm">
          {rejected
            ? "Tu solicitud fue revisada y no fue aprobada. Contacta al administrador si crees que es un error."
            : "El acceso a MCTECH EVENTOS es solo con aprobación del administrador. Completa el formulario para ser contactado y habilitar tu cuenta."}
        </p>

        {!rejected && (sent ? (
          <div className="mt-6 rounded-xl bg-[#E6F7FE] text-[#0092CC] p-4 text-sm text-center" data-testid="request-sent">
            <ShieldCheck className="mx-auto mb-2" size={22} />
            Tu solicitud está en revisión. Te avisaremos por correo cuando se habilite tu cuenta.
          </div>
        ) : (
          <div className="mt-6 space-y-3">
            <div>
              <Label className="text-sm">Teléfono / WhatsApp</Label>
              <Input data-testid="req-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+506 8888 8888" className="mt-1 rounded-lg" />
            </div>
            <div>
              <Label className="text-sm">Mensaje (opcional)</Label>
              <Textarea data-testid="req-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={3} placeholder="Cuéntanos sobre tus sorteos..." className="mt-1 rounded-lg" />
            </div>
            <Button data-testid="submit-request-btn" onClick={submit} disabled={submitting} className="w-full h-11 rounded-xl bg-[#00AEEF] hover:bg-[#0092CC]">
              {submitting ? "Enviando..." : <><Send size={16} className="mr-2" /> Enviar solicitud</>}
            </Button>
          </div>
        ))}

        <button onClick={logout} data-testid="pending-logout" className="mt-6 w-full text-sm text-neutral-400 hover:text-neutral-600 flex items-center justify-center gap-1.5">
          <LogOut size={14} /> Cerrar sesión ({user?.email})
        </button>
      </div>
    </div>
  );
}
